import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useReminders, Reminder } from '../../reminders/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentRemindersProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentReminders: React.FC<RecentRemindersProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { reminders, isLoading } = useReminders();
  const arr = Array.isArray(reminders) ? reminders : [];

  const items = React.useMemo(() => {
    return arr
      .filter((r: Reminder) => !r.clientReminderIsDone)
      .sort((a: Reminder, b: Reminder) => {
        const ta = new Date(a.clientReminderDateTime || 0).getTime();
        const tb = new Date(b.clientReminderDateTime || 0).getTime();
        return ta - tb;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Reminder>
      title="Upcoming Reminders"
      viewAllPath="/reminders"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No upcoming reminders"
      getItemKey={(r) => r.clientReminderId}
      embedded={embedded}
      renderItem={(reminder) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/reminders/${reminder.clientReminderId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' &&
            navigate(`/reminders/${reminder.clientReminderId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2`}>
              {reminder.clientReminderDescr || 'Reminder'}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {reminder.client?.clientName && `${reminder.client.clientName} · `}
              {reminder.clientReminderDateTime
                ? new Date(reminder.clientReminderDateTime).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentReminders;
